import { fetchValidatedToken } from './session'
import store from './store'
import { clearSession } from './session/actions'
import { isAuthenticated } from './session/selectors'

const API_URL =
  import.meta.env.VITE_EPC_API_URL ?? import.meta.env.REACT_APP_API_URL ?? ''

export const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  try {
    // First check if we already have a valid session before trying to validate tokens
    // This prevents trying to refresh tokens when we're not authenticated yet
    const currentState = store.getState()
    const authenticated = isAuthenticated(currentState.session)

    if (!authenticated) {
      // Skip token validation for unauthenticated users
      throw new Error('Not authenticated')
    }

    // Retrieve/refresh the token asynchronously
    const token = await fetchValidatedToken(API_URL)(store)

    // If no token is available, there's no point in making the request
    if (!token) {
      store.dispatch(clearSession({ invalidSession: true }))
      throw new Error('Authentication required')
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    }

    const response = await fetch(url, { ...options, headers })

    // Handle authentication errors explicitly
    if (response.status === 401) {
      store.dispatch(clearSession({ invalidSession: true }))
      throw new Error('Session expired')
    }

    if (!response.ok) {
      throw new Error(
        `Network response was not ok: ${response.status} ${response.statusText}`
      )
    }

    // Handle 204 No Content responses (e.g., from DELETE endpoints)
    if (response.status === 204) {
      return null
    }

    return response.json()
  } catch (error) {
    // Rethrow the error to be handled by the caller
    throw error
  }
}
